import React, { useState, useEffect } from 'react';
import { Search, User, CheckCircle, XCircle, ArrowLeft, ArrowRight, AlertTriangle, Users, GraduationCap, Info } from 'lucide-react';
import { fetchWithAuth } from '../../../../utils/api';
import { ROSTER_API } from '../../../../utils/api';
import GlowingCard from '../../../../components/ui/GlowingCard';
import FormCard from '../../../../components/ui/FormCard';

import '../RegistrationFlow.css';

// Define teacher levels with prices
const TEACHER_LEVELS = [
  { id: 'studio_owner', label: 'Studio Owner', price: 0 },
  { id: 'teacher', label: 'Teacher', price: 0 },
  { id: 'studio_assistant', label: 'Studio Assistant', price: 65 }
];

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const TeachersStep = ({
  event,
  registrationData,
  updateRegistrationData,
  onNext,
  onBack,
  isDarkMode,
  renderNavigation
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeachers, setSelectedTeachers] = useState(Array.isArray(registrationData.teachers) ? registrationData.teachers : []);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(ROSTER_API);
        const staffMembers = response.roster.filter(member => 
          member.role === 'Teacher' || member.role === 'Director'
        );
        setTeachers(staffMembers);
      } catch (err) {
        console.error('Error fetching teachers:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, []);

  const getInitials = (firstName, lastName) => {
    const initials = [];
    if (firstName) initials.push(firstName[0]);
    if (lastName) initials.push(lastName[0]);
    return initials.join('').toUpperCase();
  };

  // Get suggested teacher level based on role
  const getSuggestedTeacherLevel = (role) => {
    if (role === 'Director') return 'studio_owner';
    if (role === 'Teacher') return 'teacher';
    return null;
  };

  const toggleTeacherSelection = (teacher) => {
    const isSelected = selectedTeachers.some(t => 
      t.teacherId === teacher.teacherId || 
      t.participantId === teacher.participantId || 
      t.originalId === teacher.teacherId
    );
    
    if (isSelected) {
      // For removing a teacher, use removeParticipant if available
      if (registrationData.removeParticipant && (teacher.participantId || teacher.teacherId)) {
        registrationData.removeParticipant(teacher.participantId || teacher.teacherId)
          .then(success => {
            if (success) {
              // Update was handled by the API, but also update local state for immediate feedback
              const updatedTeachers = selectedTeachers.filter(t => 
                t.participantId !== teacher.participantId && t.teacherId !== teacher.teacherId);
              setSelectedTeachers(updatedTeachers);
            }
          })
          .catch(error => {
            console.error("Error removing teacher:", error);
          });
      } else {
        // Legacy fallback
        const updatedTeachers = selectedTeachers.filter(t => t.teacherId !== teacher.teacherId);
        
        // Update local state
        setSelectedTeachers(updatedTeachers);
        
        // Extract teacher IDs for backend reference
        const teacherIds = updatedTeachers.map(t => t.teacherId);
        
        // Update both teachers array (UI) and teacherIds (backend)
        updateRegistrationData('teachers', updatedTeachers);
        updateRegistrationData('teacherIds', teacherIds);
      }
    } else {
      // For adding a teacher, use addParticipant if available
      if (registrationData.addParticipant) {
        const newTeacher = {
          ...teacher,
          options: {
            fullConvention: true,
            training: false,
            observeOnly: false
          },
          teacherLevel: getSuggestedTeacherLevel(teacher.role)
        };
        
        registrationData.addParticipant(newTeacher)
          .then(participantId => {
            if (participantId) {
              console.log(`Added teacher with participant ID: ${participantId}`);
              // The parent component should have updated regParticipants via fetchRegistrationParticipants
              // But also update local state for immediate feedback
              const updatedTeacher = {
                ...newTeacher,
                participantId
              };
              setSelectedTeachers(prev => [...prev, updatedTeacher]);
    }
          })
          .catch(error => {
            console.error("Error adding teacher:", error);
            // Fallback: update local state if API fails
            const updatedTeachers = [...selectedTeachers, newTeacher];
            setSelectedTeachers(updatedTeachers);
          });
      } else {
        // Legacy fallback
        const updatedTeachers = [
          ...selectedTeachers, 
          {
            ...teacher,
            options: {
              fullConvention: true,
              training: false,
              observeOnly: false
            }
          }
        ];
    
    // Update local state
    setSelectedTeachers(updatedTeachers);
    
    // Extract teacher IDs for backend reference
    const teacherIds = updatedTeachers.map(t => t.teacherId);
    
    // Update both teachers array (UI) and teacherIds (backend)
    updateRegistrationData('teachers', updatedTeachers);
    updateRegistrationData('teacherIds', teacherIds);
      }
    }
  };

  const updateTeacherOptions = (teacherId, options) => {
    // Find the teacher to update
    const teacher = selectedTeachers.find(t => t.teacherId === teacherId || t.participantId === teacherId);
    if (!teacher) return;
    
    // For updating a teacher, use updateParticipant if available
    if (registrationData.updateParticipant && (teacher.participantId || teacher.teacherId)) {
      const updateData = {
        options: { ...(teacher.options || {}), ...options },
        ...options // Apply options directly too
      };
      
      registrationData.updateParticipant(teacher.participantId || teacher.teacherId, updateData)
        .then(response => {
          if (response) {
            console.log("Teacher updated via API:", response);
            // Update local state too for immediate feedback
            setSelectedTeachers(prev => prev.map(t => 
              (t.participantId === teacher.participantId || t.teacherId === teacher.teacherId)
                ? { 
                    ...t, 
                    options: { ...(t.options || {}), ...options }, 
                    ...options // Also apply options directly
                  }
                : t
            ));
          }
        })
        .catch(error => {
          console.error("Error updating teacher:", error);
        });
    } else {
      // Legacy fallback
    const updatedTeachers = selectedTeachers.map(teacher => {
      if (teacher.teacherId === teacherId) {
        return {
          ...teacher,
          options: {
            ...teacher.options,
            ...options
          }
        };
      }
      return teacher;
    });
    
    // Update local state
    setSelectedTeachers(updatedTeachers);
    
    // Extract teacher IDs for backend reference
    const teacherIds = updatedTeachers.map(t => t.teacherId);
    
    // Update both teachers array (UI) and teacherIds (backend)
    updateRegistrationData('teachers', updatedTeachers);
    updateRegistrationData('teacherIds', teacherIds);
    }
  };

  // Calculate total price for all selected teachers
  const calculateTotalPrice = () => {
    let total = 0;
    selectedTeachers.forEach(teacher => {
      if (teacher.teacherLevel) {
        const teacherLevel = TEACHER_LEVELS.find(level => level.id === teacher.teacherLevel);
        if (teacherLevel) {
          total += teacherLevel.price;
        }
      }
    });
    return total;
  };

  // Validate that all teachers have a level selected before proceeding
  const handleNext = () => {
    // Calculate final prices for teachers
    const teachersWithPricing = selectedTeachers.map(teacher => {
      // Calculate price based on selected options
      const options = teacher.options || {};
      let price = 0;
      
      if (options.fullConvention) {
        price += 200;
      }
      
      if (options.training) {
        price += 175;
      }
      
      if (options.observeOnly) {
        price += 75;
      }
      
      return {
        ...teacher,
        calculatedPrice: price
      };
    });
    
    // Extract teacher IDs for backend reference
    const teacherIds = teachersWithPricing.map(teacher => teacher.teacherId);
    
    // Update both teachers array (UI) and teacherIds (backend)
    updateRegistrationData('teachers', teachersWithPricing);
    updateRegistrationData('teacherIds', teacherIds);
    
    // Proceed to next step
    onNext();
  };

  // Filter teachers based on search query
  const filteredTeachers = teachers.filter(teacher => {
    const fullName = `${teacher.firstName} ${teacher.lastName}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  // Check if a teacher is already selected
  const isTeacherSelected = (teacherId) => {
    // Ensure selectedTeachers is an array before calling some()
    return Array.isArray(selectedTeachers) && selectedTeachers.some(teacher => teacher.teacherId === teacherId);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode 
          ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
          : 'alert alert-error shadow-lg'
      }`}>
        <AlertTriangle className="h-6 w-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full">
      {/* Fixed Header */}
      <div className={`p-4 md:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
          <div className="flex-shrink-0">
            <h2 className={`responsive-heading font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <GraduationCap className="h-6 w-6 mr-2 text-blue-500" />
              Select Teachers
            </h2>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Select teachers to register for this event
            </p>
          </div>
          
          {/* Information Alert */}
          <div className={`flex items-center p-2 px-3 rounded-lg border flex-shrink-0 md:max-w-xl lg:max-w-2xl xl:max-w-3xl ${
            isDarkMode 
              ? 'border-blue-800 text-blue-300 bg-blue-900/10' 
              : 'border-blue-200 text-blue-800 bg-blue-50'
          }`}>
            <Info className="h-5 w-5 mr-2 flex-shrink-0" />
            <p className="text-sm">
              To add new teachers, please visit the Studio page in your dashboard first.
            </p>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="p-4 md:p-6">
        <div className="space-y-8">
          {/* Search and Selected Count */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            {/* Search Box */}
            <div className={`relative flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <input
                type="text"
                className={`form-input pr-10 pl-4 py-2 w-full rounded-lg border ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-gray-200 focus:border-blue-500 focus:ring-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500'
                } focus:ring-1 focus:outline-none transition-colors duration-200`}
                placeholder="Search teachers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
            </div>
            
            {/* Selected Count */}
            <div className={`px-4 py-2 rounded-lg border ${
              isDarkMode 
                ? 'border-blue-800 text-blue-400' 
                : 'border-blue-200 text-blue-700'
            }`}>
              <span className="font-medium">{selectedTeachers.length}</span>&nbsp;{selectedTeachers.length === 1 ? 'teacher' : 'teachers'} selected
            </div>
          </div>
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Teachers */}
            <FormCard 
              title="Available Teachers" 
              subtitle={`${filteredTeachers.length} teachers found`}
              icon={<GraduationCap className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />}
              isDarkMode={isDarkMode}
            >
              <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
                {filteredTeachers.length === 0 ? (
                  <div className="text-center py-6">
                    <User className={`h-12 w-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      No teachers found
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Try adjusting your search
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTeachers.map(teacher => (
                      <AvailableTeacherCard
                        key={teacher.teacherId}
                        teacher={teacher}
                        toggleTeacherSelection={toggleTeacherSelection}
                        getInitials={getInitials}
                        isDarkMode={isDarkMode}
                        isSelected={isTeacherSelected(teacher.teacherId)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </FormCard>
            
            {/* Selected Teachers */}
            <FormCard 
              title="Selected Teachers" 
              subtitle={`${selectedTeachers.length} ${selectedTeachers.length === 1 ? 'teacher' : 'teachers'} selected`}
              icon={<CheckCircle className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />}
              isDarkMode={isDarkMode}
            >
              <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
                {selectedTeachers.length === 0 ? (
                  <div className="text-center py-6">
                    <User className={`h-12 w-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      No teachers selected yet
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Select teachers from the available list
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedTeachers.map(teacher => (
                      <SelectedTeacherCard
                        key={teacher.teacherId}
                        teacher={teacher}
                        toggleTeacherSelection={toggleTeacherSelection}
                        updateTeacherOptions={updateTeacherOptions}
                        getInitials={getInitials}
                        isDarkMode={isDarkMode}
                        eventData={event}
                        getSuggestedTeacherLevel={getSuggestedTeacherLevel}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Total Price Section */}
              {selectedTeachers.length > 0 && (
                <div className={`mt-4 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Total Teacher Fees:
                    </span>
                    <span className={`text-lg font-semibold px-3 py-1 rounded-lg ${
                      isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'
                    }`}>
                      {formatCurrency(calculateTotalPrice())}
                    </span>
                  </div>
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Studio Owners and Teachers are free, Studio Assistants are {formatCurrency(65)}
                  </p>
                </div>
              )}
            </FormCard>
          </div>
          
        {/* Render standardized navigation */}
        {renderNavigation && renderNavigation(handleNext, onBack, selectedTeachers.length > 0, "Continue")}
        </div>
      </div>
    </div>
  );
};

// Available teacher card component
const AvailableTeacherCard = ({ 
  teacher, 
  toggleTeacherSelection, 
  getInitials,
  isDarkMode,
  isSelected
}) => {
  return (
    <div 
      className={`p-3 rounded-lg border transition-all duration-200 hover:shadow-md ${
        isSelected
          ? isDarkMode 
            ? 'border-blue-800 bg-blue-900/20' 
            : 'border-blue-300 bg-blue-50'
          : isDarkMode 
            ? 'border-gray-700 hover:border-gray-600' 
            : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
            isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
          }`}>
            {getInitials(teacher.firstName, teacher.lastName)}
          </div>
          <div>
            <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {teacher.firstName} {teacher.lastName}
            </h4>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {teacher.role}
            </p>
          </div>
        </div>
        <button
          onClick={() => toggleTeacherSelection(teacher)}
          className={`p-2 rounded-full transition-colors ${
            isSelected
              ? isDarkMode
                ? 'bg-blue-500 text-white hover:bg-blue-600' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
              : isDarkMode
                ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {isSelected ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <User className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
};

// Selected teacher card component
const SelectedTeacherCard = ({ 
  teacher, 
  toggleTeacherSelection, 
  updateTeacherOptions,
  getInitials,
  isDarkMode,
  eventData,
  getSuggestedTeacherLevel
}) => {
  // Check if teacher level is selected
  const isTeacherLevelSelected = !!teacher.teacherLevel;
  
  // Get suggested level based on role
  const suggestedLevel = getSuggestedTeacherLevel(teacher.role);
  const suggestedLevelLabel = suggestedLevel ? 
    TEACHER_LEVELS.find(level => level.id === suggestedLevel)?.label : null;
  
  // Get price based on selected teacher level
  const teacherPrice = teacher.teacherLevel ? 
    TEACHER_LEVELS.find(level => level.id === teacher.teacherLevel)?.price || 0 : 0;
  
  return (
    <div className={`rounded-lg border shadow-sm ${
      !isTeacherLevelSelected
        ? isDarkMode ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-yellow-300 bg-yellow-50'
        : isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'
    }`}>
      <div className="p-3 flex flex-col">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
            }`}>
              {getInitials(teacher.firstName, teacher.lastName)}
            </div>
            <div>
              <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {teacher.firstName} {teacher.lastName}
              </h4>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                teacher.role === 'Director'
                  ? isDarkMode
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-purple-100 text-purple-800'
                  : isDarkMode
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-blue-100 text-blue-800'
              }`}>
                {teacher.role}
              </span>
            </div>
          </div>
          
          <button
            onClick={() => toggleTeacherSelection(teacher)}
            className={`p-1.5 rounded-full transition-colors ${
              isDarkMode 
                ? 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-red-400' 
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-red-500'
            }`}
          >
            <XCircle size={16} />
          </button>
        </div>
        
        {/* Teacher Level Selection */}
        <div className="mt-3">
          <label className="flex items-center justify-between text-xs font-medium mb-0.5">
            <span>
              Teacher Level {!isTeacherLevelSelected && <span className="text-yellow-600">*</span>}
            </span>
            {!isTeacherLevelSelected && (
              <span className="text-yellow-600 text-xs font-medium">Required</span>
            )}
          </label>
          <select
            value={teacher.teacherLevel || ''}
            onChange={(e) => updateTeacherOptions(teacher.teacherId, { teacherLevel: e.target.value })}
            className={`w-full p-1.5 text-xs rounded border ${
              !isTeacherLevelSelected
                ? isDarkMode 
                  ? 'border-yellow-500/50 bg-yellow-500/5 focus:border-yellow-500' 
                  : 'border-yellow-300 bg-yellow-50 focus:border-yellow-500'
                : isDarkMode
                  ? 'border-gray-700 bg-gray-800 focus:border-primary'
                  : 'border-gray-300 bg-white focus:border-primary'
            }`}
          >
            <option value="">
              {suggestedLevelLabel 
                ? `Suggested: ${suggestedLevelLabel}` 
                : "Select a teacher level"}
            </option>
            {TEACHER_LEVELS.map(level => (
              <option key={level.id} value={level.id}>
                {level.label}
              </option>
            ))}
          </select>
          
          {/* Price display */}
          {isTeacherLevelSelected && (
            <div className="flex justify-end mt-2">
              <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                teacherPrice === 0
                  ? isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800'
                  : isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'
              }`}>
                {teacherPrice === 0 ? 'Free' : formatCurrency(teacherPrice)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeachersStep; 